import kit from "@/assets/hardware/01-beedigit-kit.webp.asset.json";
import beeedge from "@/assets/hardware/02-beeedge.webp.asset.json";
import hub from "@/assets/hardware/03-beedigit-hub.webp.asset.json";
import honeyplug from "@/assets/hardware/04-honeyplug.webp.asset.json";
import beect from "@/assets/hardware/05-beect.webp.asset.json";
import beectFlexible from "@/assets/hardware/05-beect-flexible.webp.asset.json";
import padIndustrial from "@/assets/hardware/06-beepad-10-industrial.webp.asset.json";
import padMobile from "@/assets/hardware/08-beepad-mobile.webp.asset.json";
import scanDpm from "@/assets/hardware/11-beescan-dpm.webp.asset.json";
import scanWireless from "@/assets/hardware/12-beescan-wireless.webp.asset.json";
import scanWearable from "@/assets/hardware/13-beescan-wearable.webp.asset.json";
import rfidMobile from "@/assets/hardware/14-beerfid-mobile.webp.asset.json";
import rfidSled from "@/assets/hardware/15-beerfid-sled.webp.asset.json";
import rfidStation from "@/assets/hardware/16-beerfid-station.webp.asset.json";
import rfidGate from "@/assets/hardware/17-beerfid-gate.webp.asset.json";
import printDesktop from "@/assets/hardware/18-beeprint-desktop.webp.asset.json";
import printIndustrial from "@/assets/hardware/19-beeprint-industrial.webp.asset.json";
import printMobile from "@/assets/hardware/20-beeprint-mobile.webp.asset.json";
import printScanApply from "@/assets/hardware/21-beeprint-scan-apply.webp.asset.json";
import printRfid from "@/assets/hardware/22-beeprint-rfid.webp.asset.json";
import screenWide from "@/assets/hardware/23-beescreen-wide.webp.asset.json";
import screenWideAngle from "@/assets/hardware/23-beescreen-wide-angle.webp.asset.json";
import screenWideRear from "@/assets/hardware/23-beescreen-wide-rear.webp.asset.json";
import screenWideContext from "@/assets/hardware/23-beescreen-wide-context-48.webp.asset.json";
import screenIp66 from "@/assets/hardware/24-beescreen-ip66.webp.asset.json";
import screenIp66Angle from "@/assets/hardware/24-beescreen-ip66-angle.webp.asset.json";
import screenIp66Rear from "@/assets/hardware/24-beescreen-ip66-rear.webp.asset.json";
import screenIp66Context from "@/assets/hardware/24-beescreen-ip66-context-55.webp.asset.json";

export type HardwareCategory =
  | "Edge & Connectivity"
  | "Operator Terminals"
  | "Scanning"
  | "RFID"
  | "Printing & Labeling"
  | "Energy Monitoring"
  | "Industrial Displays";

export type HardwareProduct = {
  slug: string;
  name: string;
  family: string;
  category: HardwareCategory;
  eyebrow: string;
  shortDescription: string;
  positioning?: string;
  fullDescription: string;
  heroImage: string;
  imageAlt?: string;
  gallery?: { src: string; caption: string; alt: string }[];
  keyBenefits: string[];
  capabilities: { title: string; desc: string }[];
  specifications: { label: string; value: string }[];
  connectivity: string[];
  useCases: string[];
  workflows: string[];
  industries: string[];
  compatibleModules: { name: string; href: string }[];
  deploymentNotes: string;
  relatedProductSlugs: string[];
  ctaLabel?: string;
  sectionHeadings?: {
    purpose?: string;
    capabilities?: string;
    useCases?: string;
    workflows?: string;
    deployment?: string;
  };
};

const modules = {
  oee: { name: "Monitoring / OEE", href: "/modules/oee-downtime" },
  mes: { name: "MES", href: "/modules/mes" },
  quality: { name: "Quality", href: "/modules/quality-management" },
  cmms: { name: "CMMS", href: "/modules/cmms-maintenance" },
  energy: { name: "Energy", href: "/modules/energy-management" },
  dashboards: { name: "Dashboards & AI", href: "/modules/dashboards-ai" },
};

export const HARDWARE_CATEGORIES: HardwareCategory[] = [
  "Edge & Connectivity",
  "Operator Terminals",
  "Scanning",
  "RFID",
  "Printing & Labeling",
  "Energy Monitoring",
  "Industrial Displays",
];

export const HARDWARE_PRODUCTS: HardwareProduct[] = [
  {
    slug: "beedigit-kit",
    name: "BEEDIGIT Kit",
    family: "Starter bundle",
    category: "Edge & Connectivity",
    eyebrow: "Preconfigured starter bundle",
    shortDescription: "The fastest way to connect the first machine, line or bottleneck to BEEDIGIT.",
    fullDescription:
      "BEEDIGIT Kit is a preconfigured hardware bundle designed to bring the first work center online in days, not months. It pairs the BEEEDGE gateway with the machine signal or current-sensing device that best fits the equipment, adds secure connectivity and local buffering, and can be extended with a BEEPAD operator terminal, BEESCAN scanners and BEEPRINT label printers. Every kit is preconfigured for the customer's project scope, so installation is limited to mounting, wiring the sensors and validating the signals end-to-end.",
    heroImage: kit.url,
    keyBenefits: [
      "Proof of value on one line before scaling to the plant",
      "Preconfigured for the customer's machine and workflow",
      "Everything needed to capture OEE and downtime from day one",
      "Extendable with operator terminals, scanners and printers",
    ],
    capabilities: [
      { title: "Edge gateway", desc: "BEEEDGE handles protocol conversion, encryption and local store-and-forward." },
      { title: "Machine sensing", desc: "Machine signal or non-invasive current sensing depending on the equipment." },
      { title: "Secure connectivity", desc: "Ethernet, Wi-Fi or 4G with encrypted transport to the BEEDIGIT cloud." },
      { title: "Local buffering", desc: "Data is held locally when the network drops and synchronized when back online." },
      { title: "Operator options", desc: "Add a BEEPAD terminal for job orders, checklists and downtime reasons." },
      { title: "Remote validation", desc: "BEEDIGIT engineers verify every signal remotely before go-live." },
    ],
    specifications: [
      { label: "Contents", value: "BEEEDGE gateway + machine sensing device, depending on configuration" },
      { label: "Optional add-ons", value: "BEEPAD Industrial, BEESCAN, BEEPRINT" },
      { label: "Connectivity", value: "Ethernet, Wi-Fi or 4G, depending on configuration" },
      { label: "Buffering", value: "Local store-and-forward during network outages" },
      { label: "Power", value: "Industrial DC input, depending on configuration" },
      { label: "Deployment", value: "Preconfigured, remote validation included" },
    ],
    connectivity: ["Ethernet", "Wi-Fi (optional)", "4G (optional)", "MQTT over TLS to BEEDIGIT cloud"],
    useCases: [
      "First OEE pilot on a bottleneck line",
      "Downtime reason capture on a critical work center",
      "Digital job orders on the first machine",
      "Baseline energy visibility before ISO 50001",
      "Quick proof of value before a plant-wide rollout",
    ],
    workflows: [
      "Install kit → validate signals → capture live output → show OEE and downtime by shift",
      "Receive job order from ERP → confirm output on the BEEPAD → close the loop back to ERP",
    ],
    industries: ["Cable & wire", "Plastics extrusion", "Metal processing", "Food & beverage", "Discrete manufacturing"],
    compatibleModules: [modules.oee, modules.mes, modules.energy, modules.dashboards],
    deploymentNotes: "Kits are assembled and preconfigured for the specific machine, connectivity and workflow. Installation is typically completed in hours per work center.",
    relatedProductSlugs: ["beeedge", "beedigit-hub", "beepad-industrial"],
    ctaLabel: "Configure my BEEDIGIT Kit",
  },
  {
    slug: "beeedge",
    name: "BEEEDGE",
    family: "Edge gateway",
    category: "Edge & Connectivity",
    eyebrow: "Industrial edge gateway",
    shortDescription: "Connects machines, PLCs, sensors and meters to BEEDIGIT with secure, buffered transport.",
    fullDescription:
      "BEEEDGE is the industrial edge gateway that bridges the shop floor and the BEEDIGIT cloud. It collects data from machines, PLCs, meters and sensors, converts common industrial protocols, buffers data locally during network interruptions and forwards everything encrypted to the platform. Remote management reduces the need for on-site service visits for firmware, configuration and health monitoring.",
    heroImage: beeedge.url,
    keyBenefits: [
      "One edge gateway can connect supported machines, meters and sensors, depending on interfaces and configuration",
      "Secure data transmission with local buffering during network interruptions",
      "Encrypted transport to BEEDIGIT Cloud using MQTT over TLS",
      "Remote configuration and health monitoring",
    ],
    capabilities: [
      { title: "Protocol conversion", desc: "Normalizes common industrial protocols into the BEEDIGIT data model." },
      { title: "Store-and-forward", desc: "Secure data transmission with local buffering during network interruptions." },
      { title: "Secure transport", desc: "Encrypted transport to BEEDIGIT Cloud using MQTT over TLS." },
      { title: "Remote health", desc: "Device health, uptime and configuration monitored from the platform." },
      { title: "Sensor integration", desc: "Preconfigured for supported BEEDIGIT sensors and interfaces." },
      { title: "Multi-connectivity", desc: "Ethernet, Wi-Fi or 4G to match the site conditions." },
    ],
    specifications: [
      { label: "Function", value: "Industrial edge gateway" },
      { label: "Connectivity", value: "Ethernet, Wi-Fi or 4G, depending on configuration" },
      { label: "Transport", value: "MQTT over TLS" },
      { label: "Buffering", value: "Local store-and-forward during outages" },
      { label: "Management", value: "Remote configuration, health and firmware" },
      { label: "Compatible sensors", value: "BEEDIGIT HUB, HoneyPlug, BEECT and supported third-party sensors" },
    ],
    connectivity: ["Ethernet", "Wi-Fi", "4G / LTE (optional)", "MQTT/TLS"],
    useCases: [
      "Connecting legacy machines without native networking",
      "Bringing modern PLC data into the BEEDIGIT platform",
      "Bridging isolated production areas or satellite plants",
      "Reliable data collection during network interruptions",
      "Multi-sensor aggregation for one work center",
    ],
    workflows: [
      "Read machine signal / PLC tag → buffer locally → send encrypted to BEEDIGIT cloud → contextualize with job order",
      "Collect energy channel + machine state → correlate consumption to output on the platform",
    ],
    industries: ["All industrial environments", "Legacy machinery", "Multi-site groups"],
    compatibleModules: [modules.oee, modules.mes, modules.energy, modules.cmms],
    deploymentNotes: "Preconfigured for the customer's machine list, connectivity and site policies. Remote management reduces the need for on-site service visits.",
    relatedProductSlugs: ["beedigit-hub", "honeyplug", "beect-energy-sensor"],
    sectionHeadings: {
      purpose: "How BEEEDGE connects the factory floor",
      workflows: "Typical BEEEDGE data flows",
      deployment: "Recommended deployment environments",
    },
  },
  {
    slug: "beedigit-hub",
    name: "BEEDIGIT HUB",
    family: "Signal acquisition",
    category: "Edge & Connectivity",
    eyebrow: "Machine signal acquisition",
    shortDescription: "Captures production events, cycles and machine states from discrete signals and supported sensors.",
    fullDescription:
      "BEEDIGIT HUB is the signal-acquisition device for machines that expose discrete outputs, counters or state signals. It captures output pulses, run / stop / idle / alarm states, line-speed and cycle events, then forwards them to the BEEDIGIT cloud through BEEEDGE. HUB is used where the fastest and most reliable way to get production data is directly from the machine's own signals.",
    heroImage: hub.url,
    keyBenefits: [
      "Automatic, tamper-resistant production counting",
      "Micro-stop detection at the signal level",
      "Simple retrofit on legacy and modern equipment",
      "Feeds live OEE, downtime and cycle-time analytics",
    ],
    capabilities: [
      { title: "Output & counters", desc: "Captures output pulses and counter values from the machine." },
      { title: "State signals", desc: "Reads run, stop, idle and alarm signals from discrete outputs." },
      { title: "Cycle events", desc: "Detects cycle and line-speed events for cycle-time analysis." },
      { title: "Sensor input", desc: "Connects supported discrete sensors alongside machine signals." },
      { title: "Secure forwarding", desc: "Streams to the BEEDIGIT cloud through BEEEDGE." },
      { title: "Retrofit friendly", desc: "Installs without modifying the machine's control logic." },
    ],
    specifications: [
      { label: "Function", value: "Machine signal acquisition" },
      { label: "Inputs", value: "Discrete signals and supported sensors, depending on configuration" },
      { label: "Uplink", value: "Through BEEEDGE gateway" },
      { label: "Installation", value: "Non-intrusive on the machine controller" },
      { label: "Power", value: "Industrial DC input, depending on configuration" },
    ],
    connectivity: ["Discrete I/O", "Sensor input", "Uplink via BEEEDGE"],
    useCases: [
      "Automatic production counting on any machine with an output signal",
      "OEE and downtime monitoring for a full line",
      "Micro-stop detection where operators under-report",
      "Cycle-time validation on assembly stations",
      "Line-speed tracking on continuous processes",
    ],
    workflows: [
      "Capture output pulse → classify as good / scrap → update live OEE",
      "Detect stop signal → prompt operator on the BEEPAD to classify the downtime reason",
    ],
    industries: ["Cable & wire", "Plastics extrusion", "Metal processing", "Packaging", "Food & beverage"],
    compatibleModules: [modules.oee, modules.mes, modules.dashboards],
    deploymentNotes: "Wired to available machine signals and validated end-to-end before go-live.",
    relatedProductSlugs: ["beeedge", "honeyplug", "beedigit-kit"],
  },
  {
    slug: "honeyplug",
    name: "HoneyPlug",
    family: "Non-invasive sensing",
    category: "Edge & Connectivity",
    eyebrow: "Non-invasive current sensing",
    shortDescription: "Non-invasive machine-state detection using current sensing, designed for fast retrofits where direct PLC integration is unavailable or unnecessary.",
    fullDescription:
      "HoneyPlug is a non-invasive current-sensing device that infers machine state from electrical consumption. It is designed for fast retrofit projects on legacy equipment or cases where a full PLC integration is disproportionate to the goal. HoneyPlug is often used to bring an entire line online for OEE and downtime monitoring in a single visit.",
    heroImage: honeyplug.url,
    keyBenefits: [
      "Retrofit on legacy machines without touching controls",
      "Fastest path to run/stop visibility",
      "No downtime required to install",
      "Pairs with BEEEDGE for secure forwarding",
    ],
    capabilities: [
      { title: "Run/stop detection", desc: "Infers machine activity from current draw." },
      { title: "Non-invasive current sensing", desc: "Clamps on without modifying the machine." },
      { title: "Fast retrofit", desc: "Ideal for putting a full workshop online in a single visit." },
    ],
    specifications: [
      { label: "Function", value: "Non-invasive current sensing" },
      { label: "Installation", value: "Non-invasive clamp-on, depending on configuration" },
      { label: "Uplink", value: "Through BEEEDGE gateway" },
      { label: "Best for", value: "Legacy machines, quick pilots" },
    ],
    connectivity: ["Clamp-on current sensing", "Uplink via BEEEDGE"],
    useCases: [
      "Bringing a legacy workshop online in a single visit",
      "Run/stop and idle-time visibility on machines without controllers",
      "Fast pilot installations before deeper integration",
      "Energy-related loss analysis at the machine level",
      "Bridging old lines while modern machines are integrated via BEEDIGIT HUB",
    ],
    workflows: [
      "Clamp HoneyPlug on the machine feed → infer run / idle / stop → send state to BEEDIGIT cloud",
      "Detect prolonged idle → surface as loss on the shift dashboard",
    ],
    industries: ["Legacy production", "Small & mid-size manufacturers", "Wood & furniture", "Metal processing"],
    compatibleModules: [modules.oee, modules.energy],
    deploymentNotes: "Installed without modifying the machine controller, typically in minutes per asset.",
    relatedProductSlugs: ["beedigit-hub", "beect-energy-sensor", "beeedge"],
  },
  {
    slug: "beect-energy-sensor",
    name: "BEECT",
    family: "Split-core current transformers",
    category: "Energy Monitoring",
    eyebrow: "Split-core current transformers",
    shortDescription: "Split-core current transformers for non-invasive monitoring of machine feeders, panel circuits and selected production loads.",
    fullDescription:
      "BEECT is a family of split-core current transformers designed for retrofit energy monitoring. Different sensor sizes support different conductor and installation requirements, from compact machine circuits to large electrical feeders. The family includes SCT010 for compact installations and smaller conductors, SCT024TS for medium-size conductors and common machine feeders, and SCT080B for large conductors, main feeders and larger electrical installations. BEECT measures electrical current; BEEDIGIT software correlates the collected data with machines, orders, batches, shifts and production states.",
    heroImage: beect.url,
    imageAlt: "BEECT split-core current transformer family — SCT080B, SCT024TS and SCT010",
    keyBenefits: [
      "Split-core installation on existing conductors",
      "Multiple sensor sizes for different conductor requirements",
      "Non-invasive retrofit on machine feeders and panel circuits",
      "Feeds BEEDIGIT to correlate current with orders, batches and shifts",
    ],
    capabilities: [
      { title: "Split-core installation", desc: "Sensors open around the conductor without disconnecting wiring." },
      { title: "Multiple conductor sizes", desc: "SCT010, SCT024TS and SCT080B cover compact to large conductors." },
      { title: "Machine and circuit monitoring", desc: "Installed on machine feeders, panel circuits and selected production loads." },
      { title: "Retrofit friendly", desc: "Designed for existing panels and installed machines." },
      { title: "Software correlation", desc: "BEEDIGIT correlates current data with machines, orders, batches, shifts and production states." },
    ],
    specifications: [
      { label: "Function", value: "Split-core current transformers" },
      { label: "Family", value: "SCT010, SCT024TS, SCT080B" },
      { label: "SCT010", value: "Compact installations and smaller conductors" },
      { label: "SCT024TS", value: "Medium-size conductors and common machine feeders" },
      { label: "SCT080B", value: "Large conductors, main feeders and larger electrical installations" },
      { label: "Installation", value: "Split-core, non-invasive on existing conductors" },
      { label: "Uplink", value: "Through BEEEDGE gateway" },
    ],
    connectivity: ["Split-core current transformer input", "Uplink via BEEEDGE"],
    useCases: [
      "Individual machine energy monitoring",
      "Machine feeder monitoring",
      "Electrical panel circuit monitoring",
      "Production-line energy monitoring",
      "Retrofit energy-monitoring projects",
      "Energy visibility by machine or production area",
    ],
    workflows: [
      "Install split-core sensor on the conductor → uplink through BEEEDGE → BEEDIGIT correlates current with machine, order and shift",
      "Monitor circuit current → visualize by machine and production area in BEEDIGIT dashboards",
    ],
    industries: ["Energy-intensive manufacturing", "Cable & wire", "Plastics extrusion", "Metal processing", "Food & beverage"],
    compatibleModules: [modules.energy, modules.oee, modules.dashboards],
    deploymentNotes: "Sensor size (SCT010, SCT024TS or SCT080B) is selected for the target conductor. For large conductors, busbars or crowded panels where a rigid split-core sensor is difficult to install, use BEECT Flexible.",
    relatedProductSlugs: ["beect-flexible", "beeedge", "beedigit-hub"],
  },
  {
    slug: "beect-flexible",
    name: "BEECT Flexible",
    family: "Flexible current sensors",
    category: "Energy Monitoring",
    eyebrow: "Flexible current sensors",
    shortDescription: "Flexible current sensors for large conductors, busbars and crowded electrical panels where rigid split-core sensors are difficult to install.",
    fullDescription:
      "BEECT Flexible is designed for energy-monitoring installations where conductor size, busbar geometry or available panel space makes a rigid split-core current transformer impractical. Flexible sensing loops support installation around large cables, grouped conductors and selected busbar configurations. BEECT Flexible measures electrical current; BEEDIGIT software correlates the collected data with machines, orders, batches, shifts and production states.",
    heroImage: beectFlexible.url,
    imageAlt: "BEECT Flexible current sensors with measurement module",
    keyBenefits: [
      "Flexible loop installation around large conductors and busbars",
      "Fits crowded panels where rigid sensors do not",
      "Retrofit on existing electrical installations",
      "Feeds BEEDIGIT to correlate current with orders, batches and shifts",
    ],
    capabilities: [
      { title: "Flexible loop installation", desc: "Sensing loops route around large cables, grouped conductors and selected busbar configurations." },
      { title: "Large conductors and busbars", desc: "Suited to main feeders and high-current equipment where split-core sensors are difficult to fit." },
      { title: "Crowded panel retrofits", desc: "Designed for installations with limited space around the conductor." },
      { title: "Software correlation", desc: "BEEDIGIT correlates current data with machines, orders, batches, shifts and production states." },
    ],
    specifications: [
      { label: "Function", value: "Flexible current sensors" },
      { label: "Installation", value: "Flexible loop around large conductors, grouped conductors or selected busbar configurations" },
      { label: "Best for", value: "Large conductors, busbars, irregular geometries and limited-space panels" },
      { label: "Uplink", value: "Through BEEEDGE gateway" },
    ],
    connectivity: ["Flexible current sensor input", "Uplink via BEEEDGE"],
    useCases: [
      "Large machine feeders",
      "Main electrical incomers",
      "Busbar monitoring",
      "High-current production equipment",
      "Crowded electrical panels",
      "Irregular conductor geometry",
      "Retrofit projects with limited installation space",
    ],
    workflows: [
      "Route flexible loop around the conductor or busbar → uplink through BEEEDGE → BEEDIGIT correlates current with machine, order and shift",
      "Monitor main feeder current → visualize by machine and production area in BEEDIGIT dashboards",
    ],
    industries: ["Energy-intensive manufacturing", "Cable & wire", "Metal processing", "Plastics extrusion", "Food & beverage"],
    compatibleModules: [modules.energy, modules.oee, modules.dashboards],
    deploymentNotes: "Loop size and configuration are selected for the target conductor or busbar. Installation is planned around site electrical safety procedures.",
    relatedProductSlugs: ["beect-energy-sensor", "beeedge", "beedigit-hub"],
  },
  {
    slug: "beepad-industrial",
    name: "BEEPAD Industrial",
    family: "Operator terminal",
    category: "Operator Terminals",
    eyebrow: "Rugged shop-floor tablet",
    shortDescription: "Large-screen rugged operator tablet for production execution and visual management.",
    fullDescription:
      "BEEPAD Industrial is a rugged large-screen operator terminal designed for production execution, digital work instructions and visual management. It supports a 10.1-inch-class display, glove and wet-touch operation, optional integrated 1D/2D scanning and industrial protection depending on configuration, so it can live on the shop floor next to the machine it serves.",
    heroImage: padIndustrial.url,
    keyBenefits: [
      "One terminal for job orders, checklists and Andon",
      "Glove and wet-touch operation available on selected configurations",
      "Large screen for visual management at the machine",
      "Configurable with integrated scanning",
    ],
    capabilities: [
      { title: "Job-order execution", desc: "Operators receive, start and confirm job orders directly at the machine." },
      { title: "Digital work instructions", desc: "Show the right instructions for the right operation." },
      { title: "Quality checks", desc: "In-process checklists and non-conformance capture." },
      { title: "Setup & maintenance", desc: "Setup validation and maintenance requests from the shop floor." },
      { title: "Andon", desc: "One-tap calls to supervisors, maintenance or quality." },
      { title: "Shift dashboards", desc: "Live OEE, downtime and plan-versus-actual production visibility on-screen." },
    ],
    specifications: [
      { label: "Display", value: "10.1-inch class, depending on configuration" },
      { label: "Input", value: "Glove and wet-touch capable" },
      { label: "Scanning", value: "Integrated 1D/2D scanner available as an option" },
      { label: "Protection", value: "Industrial protection rating, depending on configuration" },
      { label: "Connectivity", value: "Wi-Fi and Ethernet dock options, depending on configuration" },
      { label: "Mounting", value: "Wall, arm or stand mounts available as an option" },
    ],
    connectivity: ["Wi-Fi", "Optional Ethernet dock", "Optional integrated 1D/2D scanning"],
    useCases: [
      "Job-order execution and output confirmation at the machine",
      "Digital work instructions for setups and changeovers",
      "In-process quality checks and non-conformance capture",
      "Andon calls and issue escalation",
      "Shift start-up, hand-over and daily management",
    ],
    workflows: [
      "Operator scans job → BEEPAD opens the work instruction → confirms output and consumption",
      "Machine stops → BEEPAD prompts for downtime reason → reason feeds live OEE and Pareto",
    ],
    industries: ["Discrete manufacturing", "Automotive components", "Food & beverage", "Metal processing", "Plastics extrusion"],
    compatibleModules: [modules.mes, modules.oee, modules.quality, modules.cmms],
    deploymentNotes: "Delivered with the BEEDIGIT operator app preconfigured for the customer's roles, work centers and workflows.",
    relatedProductSlugs: ["beepad-mobile", "beescan-wireless", "beedigit-kit"],
  },
  {
    slug: "beepad-mobile",
    name: "BEEPAD Mobile",
    family: "Handheld terminal",
    category: "Operator Terminals",
    eyebrow: "Rugged handheld terminal",
    shortDescription: "Handheld Android terminal with professional 1D/2D scanning for operators and warehouse teams.",
    fullDescription:
      "BEEPAD Mobile is a rugged handheld terminal for operators, warehouse and quality teams who move across the plant. It combines professional 1D/2D scanning with the BEEDIGIT operator app to keep material flow, WIP movement and traceability recorded in real time.",
    heroImage: padMobile.url,
    keyBenefits: [
      "Real-time WIP and material movement",
      "Professional scanning without extra hardware",
      "One device for material, quality and finished-goods flows",
      "Rugged enough for daily industrial use",
    ],
    capabilities: [
      { title: "Professional scanning", desc: "Integrated 1D/2D scan engine for QR and barcode capture." },
      { title: "Material flow", desc: "Issue, return and move material tied to job orders." },
      { title: "WIP movement", desc: "Track intermediate batches between work centers." },
      { title: "Batch and serial capture", desc: "Maintain full genealogy from raw material to finished good." },
      { title: "Finished-goods confirmation", desc: "Confirm production and prepare labels at the point of work." },
      { title: "Inventory checks", desc: "Cycle counts and inventory validation on the move." },
    ],
    specifications: [
      { label: "Form factor", value: "Rugged handheld" },
      { label: "OS", value: "Android, depending on configuration" },
      { label: "Scanning", value: "Integrated 1D/2D scan engine" },
      { label: "Connectivity", value: "Wi-Fi and 4G options, depending on configuration" },
      { label: "Protection", value: "Industrial rating, depending on configuration" },
    ],
    connectivity: ["Wi-Fi", "4G / LTE (optional)", "Integrated 1D/2D scanning"],
    useCases: [
      "QR traceability across the plant",
      "Material issue and return tied to job orders",
      "WIP movement between work centers",
      "Batch and serial capture on quality gates",
      "Finished-goods confirmation and dispatch preparation",
    ],
    workflows: [
      "Scan pallet → confirm move → BEEDIGIT updates WIP location instantly",
      "Scan raw material → validate against BOM → confirm consumption",
    ],
    industries: ["Food & beverage", "Automotive components", "Metal processing", "Packaging", "Discrete manufacturing"],
    compatibleModules: [modules.mes, modules.quality, modules.dashboards],
    deploymentNotes: "Enrolled and preconfigured with the BEEDIGIT operator app and the customer's user roles.",
    relatedProductSlugs: ["beepad-industrial", "beescan-wireless", "beerfid-mobile"],
  },
  {
    slug: "beescan-dpm",
    name: "BEESCAN DPM",
    family: "DPM scanner",
    category: "Scanning",
    eyebrow: "Direct-part-mark scanner",
    shortDescription: "Ultra-rugged wired scanner for 1D, 2D and direct-part-mark codes on difficult surfaces.",
    fullDescription:
      "BEESCAN DPM is an ultra-rugged wired scanner for reading 1D, 2D and direct-part-mark codes on difficult surfaces. It handles laser engraving, dot peening, chemical etching and ink marking on metal, plastic and machined parts, and is designed to survive production-line use over long shifts.",
    heroImage: scanDpm.url,
    keyBenefits: [
      "Reads DPM codes that consumer scanners miss",
      "Wired reliability at quality gates",
      "Handles high-contrast and low-contrast marks",
      "Built for continuous production use",
    ],
    capabilities: [
      { title: "DPM reading", desc: "Laser, dot-peen, chemical-etch and ink marks on demanding surfaces." },
      { title: "1D & 2D", desc: "Reads standard 1D and 2D codes alongside DPM." },
      { title: "Rugged wired scanner", desc: "USB and serial connectivity options, depending on configuration." },
      { title: "Fast trigger", desc: "Reliable first-scan reads at quality gates." },
      { title: "Long-shift durable", desc: "Engineered for production-line durability." },
    ],
    specifications: [
      { label: "Type", value: "Wired handheld 1D / 2D / DPM scanner" },
      { label: "DPM support", value: "Laser, dot-peen, chemical-etch, ink marking" },
      { label: "Connectivity", value: "USB and serial options, depending on configuration" },
      { label: "Durability", value: "Production-line rated, depending on configuration" },
    ],
    connectivity: ["USB", "Serial (optional)"],
    useCases: [
      "Machined-part traceability",
      "Automotive component identification",
      "Metal and electronics production",
      "Work-center verification at the operation start",
      "Quality-gate scanning on inspection stations",
    ],
    workflows: [
      "Scan DPM on machined part → validate against job order → open the correct operation",
      "Scan at quality gate → tie inspection result to serial in BEEDIGIT",
    ],
    industries: ["Automotive components", "Metal processing", "Discrete manufacturing", "Electronics"],
    compatibleModules: [modules.mes, modules.quality, modules.dashboards],
    deploymentNotes: "Configured for the customer's mark types, connectivity and station layout.",
    relatedProductSlugs: ["beescan-wireless", "beescan-wearable", "beepad-industrial"],
  },
  {
    slug: "beescan-wireless",
    name: "BEESCAN Wireless",
    family: "Wireless scanner",
    category: "Scanning",
    eyebrow: "Industrial wireless scanner",
    shortDescription: "Wireless 1D/2D scanner for cable-free workstations, packaging lines and warehouse operations.",
    fullDescription:
      "BEESCAN Wireless is an industrial 1D/2D scanner for workflows that require freedom of movement — packaging lines, quality stations, kitting, dispatch and warehouse operations. It docks to a base station and integrates directly with BEEDIGIT operator workflows.",
    heroImage: scanWireless.url,
    keyBenefits: [
      "Cable-free scanning at every workstation",
      "Fast reads across packaging and warehouse flows",
      "Long shift battery life",
      "Docks straight into BEEDIGIT execution",
    ],
    capabilities: [
      { title: "1D & 2D reading", desc: "Reliable capture for QR, barcode and stacked codes." },
      { title: "Wireless mobility", desc: "Freedom to move around workstations, pallets and lines." },
      { title: "Base station docking", desc: "Charge and pair on a shared base for the workstation." },
      { title: "Batch mode", desc: "Continue scanning when out of range, depending on configuration." },
    ],
    specifications: [
      { label: "Type", value: "Wireless 1D / 2D scanner" },
      { label: "Wireless", value: "Bluetooth to base station, depending on configuration" },
      { label: "Battery", value: "Full-shift use, depending on configuration" },
      { label: "Durability", value: "Industrial rated, depending on configuration" },
    ],
    connectivity: ["Bluetooth to base station", "USB from base to host"],
    useCases: [
      "Packaging and pallet verification",
      "Material picking on the shop floor",
      "Finished-goods scanning at dispatch",
      "Quality and rework stations",
      "Loading and dispatch verification",
    ],
    workflows: [
      "Scan pallet → confirm move → BEEDIGIT updates WIP or FG location",
      "Scan finished good at dispatch → verify against sales order → release shipment",
    ],
    industries: ["Food & beverage", "Packaging", "Automotive components", "Metal processing", "Discrete manufacturing"],
    compatibleModules: [modules.mes, modules.dashboards, modules.quality],
    deploymentNotes: "Base stations and scanners are preconfigured for the customer's workstations and workflows.",
    relatedProductSlugs: ["beescan-dpm", "beescan-wearable", "beepad-mobile"],
  },
  {
    slug: "beescan-wearable",
    name: "BEESCAN Wearable",
    family: "Wearable scanner",
    category: "Scanning",
    eyebrow: "Hands-free scanning",
    shortDescription: "Wearable computer and ring-scanner solution for high-frequency, hands-free scanning.",
    fullDescription:
      "BEESCAN Wearable pairs a small wearable computer with a ring scanner so operators keep both hands available while scanning at high frequency. It is designed for kitting, assembly-line picking, warehouse replenishment and sequencing workflows where every second of hand movement matters.",
    heroImage: scanWearable.url,
    keyBenefits: [
      "Both hands free for picking and assembly",
      "Higher scan frequency without operator fatigue",
      "Fewer errors in sequencing and kitting",
      "Full-shift wearable comfort",
    ],
    capabilities: [
      { title: "Ring scanner", desc: "Trigger scan without picking up a device." },
      { title: "Wearable computer", desc: "Compact terminal worn on the wrist or arm, depending on configuration." },
      { title: "1D & 2D reading", desc: "Handles QR and standard barcodes." },
      { title: "BEEDIGIT integration", desc: "Runs the BEEDIGIT operator flow directly on the wearable." },
    ],
    specifications: [
      { label: "Type", value: "Wearable computer + ring scanner" },
      { label: "Wearable form", value: "Wrist or arm, depending on configuration" },
      { label: "Wireless", value: "Bluetooth between ring and wearable" },
      { label: "Battery", value: "Full-shift use, depending on configuration" },
    ],
    connectivity: ["Bluetooth (ring ↔ wearable)", "Wi-Fi from wearable to network"],
    useCases: [
      "Kitting for assembly lines",
      "Assembly-line picking and sequencing",
      "Warehouse replenishment",
      "Component sequencing at the workstation",
      "High-volume inventory counting",
    ],
    workflows: [
      "Scan pick location → confirm quantity → move to next pick without picking up a device",
      "Sequence components against the job → BEEDIGIT validates order and completeness",
    ],
    industries: ["Automotive components", "Discrete manufacturing", "Electronics", "Wood & furniture"],
    compatibleModules: [modules.mes, modules.dashboards],
    deploymentNotes: "Kits are preconfigured for the operator role, work center and picking flow.",
    relatedProductSlugs: ["beescan-wireless", "beescan-dpm", "beerfid-mobile"],
  },
  {
    slug: "beerfid-mobile",
    name: "BEERFID Mobile",
    family: "Handheld RFID",
    category: "RFID",
    eyebrow: "Handheld RFID + barcode",
    shortDescription: "Rugged handheld with integrated UHF RFID and barcode capture for fast, accurate identification.",
    fullDescription:
      "BEERFID Mobile is a rugged handheld computer with integrated UHF RFID and barcode capture. It is designed for rapid inventory counts, tool and asset tracking, and WIP identification, and integrates directly with BEEDIGIT operator workflows.",
    heroImage: rfidMobile.url,
    keyBenefits: [
      "One device for RFID and barcode capture",
      "Rapid inventory and asset counts",
      "Live WIP and container identification",
      "Rugged handheld built for daily industrial use",
    ],
    capabilities: [
      { title: "UHF RFID reading", desc: "Reads and writes UHF tags at practical distances." },
      { title: "Barcode capture", desc: "1D/2D scanning as a fallback and for mixed workflows." },
      { title: "Inventory mode", desc: "Bulk-count large sets of tags in seconds." },
      { title: "Asset tracking", desc: "Ties assets to locations, users and work orders." },
    ],
    specifications: [
      { label: "Form factor", value: "Rugged handheld" },
      { label: "RFID", value: "UHF, depending on configuration" },
      { label: "Barcode", value: "Integrated 1D/2D scan engine" },
      { label: "Connectivity", value: "Wi-Fi and 4G options, depending on configuration" },
    ],
    connectivity: ["UHF RFID", "1D/2D barcode", "Wi-Fi", "4G / LTE (optional)"],
    useCases: [
      "Rapid inventory counting on the shop floor",
      "Tool and asset tracking across departments",
      "WIP identification without line-of-sight",
      "Returnable-container tracking",
      "Batch and pallet traceability",
    ],
    workflows: [
      "Sweep RFID over a rack → capture all tags → update inventory in BEEDIGIT",
      "Scan asset → assign to user and location → track movement automatically",
    ],
    industries: ["Automotive components", "Metal processing", "Discrete manufacturing", "Wood & furniture", "Logistics"],
    compatibleModules: [modules.mes, modules.cmms, modules.dashboards],
    deploymentNotes: "Configured for the customer's tag frequency, security policy and BEEDIGIT workflows.",
    relatedProductSlugs: ["beerfid-sled", "beerfid-station", "beerfid-gate"],
  },
  {
    slug: "beerfid-sled",
    name: "BEERFID Sled",
    family: "RFID sled",
    category: "RFID",
    eyebrow: "RFID reader attachment",
    shortDescription: "High-performance UHF RFID reader attachment that adds long-range reading to mobile terminals.",
    fullDescription:
      "BEERFID Sled attaches to a compatible mobile terminal to add high-performance UHF RFID reading. It is used for warehouse cycle counts, pallet identification, yard verification and any workflow where long-range reading of many tags at once is required.",
    heroImage: rfidSled.url,
    keyBenefits: [
      "Long-range reading of many tags at once",
      "Extends existing mobile terminals",
      "Speeds up cycle counts and dispatch checks",
      "One integrated tool for the operator",
    ],
    capabilities: [
      { title: "Long-range UHF", desc: "Reads tags at practical warehouse and yard distances." },
      { title: "Bulk reading", desc: "Processes many tags in a single sweep." },
      { title: "Sled attachment", desc: "Snaps to compatible terminals for one-hand operation." },
    ],
    specifications: [
      { label: "Type", value: "UHF RFID sled attachment" },
      { label: "Range", value: "Long-range UHF, depending on tag and environment" },
      { label: "Host", value: "Compatible mobile terminal, depending on configuration" },
    ],
    connectivity: ["UHF RFID", "Attaches to compatible mobile terminal"],
    useCases: [
      "Long-range tag reading in warehouses",
      "Warehouse cycle counts",
      "Pallet and container identification",
      "Yard and dispatch verification",
      "Bulk asset checks in maintenance yards",
    ],
    workflows: [
      "Walk warehouse aisle → sled captures all tags in range → BEEDIGIT reconciles inventory",
      "Verify pallet load at dispatch → compare against dispatch order",
    ],
    industries: ["Automotive components", "Metal processing", "Logistics", "Manufacturing groups with warehousing"],
    compatibleModules: [modules.mes, modules.dashboards],
    deploymentNotes: "Paired with compatible mobile terminals and preconfigured for the customer's tag setup.",
    relatedProductSlugs: ["beerfid-mobile", "beerfid-gate", "beerfid-station"],
  },
  {
    slug: "beerfid-station",
    name: "BEERFID Station",
    family: "RFID station",
    category: "RFID",
    eyebrow: "Desktop RFID station",
    shortDescription: "Compact fixed or desktop UHF RFID station for controlled short-range identification.",
    fullDescription:
      "BEERFID Station is a compact UHF RFID reader for controlled short-range identification. It is typically used at issue and return stations, tool rooms, material checkpoints and work-center input/output confirmation, where only the tag placed on the station should be read.",
    heroImage: rfidStation.url,
    keyBenefits: [
      "Precise, controlled reads at a fixed point",
      "No hand scanning needed for repeat operations",
      "Reliable tag association at issue/return",
      "Simple deployment on any workstation",
    ],
    capabilities: [
      { title: "Short-range UHF", desc: "Reads only the tag placed on or near the station." },
      { title: "Fixed workflow", desc: "Ideal for repetitive issue, return or checkpoint operations." },
      { title: "Workstation integration", desc: "Plugs into a workstation running BEEDIGIT." },
    ],
    specifications: [
      { label: "Type", value: "Desktop / fixed UHF RFID reader" },
      { label: "Range", value: "Controlled short-range" },
      { label: "Connectivity", value: "USB or Ethernet, depending on configuration" },
    ],
    connectivity: ["USB", "Ethernet (optional)"],
    useCases: [
      "Tool issue and return stations",
      "Material checkpoints",
      "Work-center input/output confirmation",
      "Controlled tag association during setup",
      "Access control for tagged assets",
    ],
    workflows: [
      "Place tool on station → BEEDIGIT logs issue to the operator",
      "Return tool to station → BEEDIGIT closes the loan and updates availability",
    ],
    industries: ["Automotive components", "Metal processing", "Discrete manufacturing", "Maintenance-heavy plants"],
    compatibleModules: [modules.mes, modules.cmms],
    deploymentNotes: "Preconfigured for the workflow the station serves (tools, materials, WIP, access).",
    relatedProductSlugs: ["beerfid-mobile", "beerfid-gate", "beerfid-sled"],
  },
  {
    slug: "beerfid-gate",
    name: "BEERFID Gate",
    family: "RFID gate",
    category: "RFID",
    eyebrow: "Fixed RFID gate",
    shortDescription: "Fixed multi-antenna RFID infrastructure for automated movement detection.",
    fullDescription:
      "BEERFID Gate is fixed multi-antenna RFID infrastructure that detects tagged items automatically as they pass through production, warehouse or dispatch zones. It removes the need for manual scanning where high-frequency, unattended detection is required.",
    heroImage: rfidGate.url,
    keyBenefits: [
      "Automated, unattended movement detection",
      "Frees operators from manual scans",
      "Reliable dock-door and zone verification",
      "Fits into existing logistics flows",
    ],
    capabilities: [
      { title: "Multi-antenna gate", desc: "Configurable antennas cover the passage width." },
      { title: "Automated detection", desc: "Reads every tagged item passing through without manual action." },
      { title: "Zone verification", desc: "Confirms entry, exit or transfer between zones." },
      { title: "Dock-door integration", desc: "Feeds BEEDIGIT logistics and traceability flows." },
    ],
    specifications: [
      { label: "Type", value: "Fixed multi-antenna UHF RFID gate" },
      { label: "Coverage", value: "Passage width configurable" },
      { label: "Connectivity", value: "Ethernet, depending on configuration" },
    ],
    connectivity: ["UHF RFID", "Ethernet"],
    useCases: [
      "Automated WIP movement between production areas",
      "Dock-door verification for inbound and outbound",
      "Pallet flow tracking through logistics zones",
      "Entry/exit control for restricted areas",
      "Reusable asset tracking across plants",
    ],
    workflows: [
      "Pallet crosses gate → BEEDIGIT logs the movement with time, direction and load",
      "Asset leaves an area → gate raises exception if not authorized",
    ],
    industries: ["Logistics", "Manufacturing groups", "Automotive components", "Food & beverage"],
    compatibleModules: [modules.mes, modules.dashboards],
    deploymentNotes: "Site survey and antenna configuration are part of the deployment.",
    relatedProductSlugs: ["beerfid-mobile", "beerfid-sled", "beerfid-station"],
  },
  {
    slug: "beeprint-desktop",
    name: "BEEPRINT Desktop",
    family: "Desktop label printer",
    category: "Printing & Labeling",
    eyebrow: "Compact label printer",
    shortDescription: "Compact direct-thermal label printer for work centers, quality stations and low-to-medium volumes.",
    fullDescription:
      "BEEPRINT Desktop is a compact direct-thermal label printer designed for work centers, quality stations and low-to-medium volume QR and barcode printing. It is small enough to live at the point of work while producing labels that meet industrial expectations.",
    heroImage: printDesktop.url,
    keyBenefits: [
      "Point-of-work labeling",
      "Small footprint for busy workstations",
      "Consistent QR and barcode quality",
      "Integrates with BEEDIGIT execution",
    ],
    capabilities: [
      { title: "Direct-thermal printing", desc: "Media-driven printing without ribbons." },
      { title: "QR and barcode", desc: "Reliable output for traceability labels." },
      { title: "Workstation footprint", desc: "Fits on quality and work-center desks." },
      { title: "BEEDIGIT integration", desc: "Prints on demand from the operator app." },
    ],
    specifications: [
      { label: "Type", value: "Compact direct-thermal label printer" },
      { label: "Print width", value: "Up to 4-inch, depending on configuration" },
      { label: "Connectivity", value: "USB and Ethernet options, depending on configuration" },
      { label: "Media", value: "Roll media, depending on configuration" },
    ],
    connectivity: ["USB", "Ethernet (optional)"],
    useCases: [
      "WIP labels at the work center",
      "Batch labels for in-process material",
      "Material identification labels",
      "Quality-status labels at inspection stations",
      "Shipping and pallet labels for lower volumes",
    ],
    workflows: [
      "Confirm production → BEEDIGIT triggers a QR label at the workstation → operator applies it to the batch",
    ],
    industries: ["Discrete manufacturing", "Food & beverage", "Metal processing", "Small & mid-size manufacturers"],
    compatibleModules: [modules.mes, modules.quality],
    deploymentNotes: "Preconfigured for the customer's label templates and workstation placement.",
    relatedProductSlugs: ["beeprint-industrial", "beeprint-mobile", "beeprint-rfid"],
  },
  {
    slug: "beeprint-industrial",
    name: "BEEPRINT Industrial",
    family: "Industrial label printer",
    category: "Printing & Labeling",
    eyebrow: "Heavy-duty label printer",
    shortDescription: "4-inch thermal transfer and direct thermal printer for continuous, high-volume production.",
    fullDescription:
      "BEEPRINT Industrial is a heavy-duty 4-inch thermal transfer and direct thermal printer designed for high-volume industrial label printing. It is available in 203 dpi and 300 dpi configurations, supports large media capacity and industrial connectivity, with wireless options depending on configuration. Durability of the printed label depends on the selected label material and ribbon.",
    heroImage: printIndustrial.url,
    keyBenefits: [
      "Designed for high-volume industrial label printing",
      "Durable, long-life label options",
      "Supports durable labels when paired with appropriate media and ribbons",
      "Fits industrial print rooms and lines",
    ],
    capabilities: [
      { title: "Thermal transfer and direct thermal", desc: "Both printing modes supported, depending on media and configuration." },
      { title: "Resolution options", desc: "203 dpi and 300 dpi configurations." },
      { title: "Large media capacity", desc: "Long production runs between reloads." },
      { title: "Industrial connectivity", desc: "USB, Ethernet and serial, with Wi-Fi or Bluetooth as options." },
    ],
    specifications: [
      { label: "Type", value: "Industrial thermal transfer / direct thermal printer" },
      { label: "Print width", value: "Up to 4-inch" },
      { label: "Resolution", value: "203 dpi or 300 dpi, depending on configuration" },
      { label: "Connectivity", value: "USB, Ethernet, serial; Wi-Fi or Bluetooth optional" },
      { label: "Media", value: "Large-capacity roll, depending on configuration" },
    ],
    connectivity: ["USB", "Ethernet", "Serial", "Wi-Fi (optional)", "Bluetooth (optional)"],
    useCases: [
      "High-volume production labels",
      "Durable product identification labels",
      "Long-life labels when paired with appropriate media and ribbons",
      "Cable, drum and reel labeling",
      "Warehouse and dispatch labeling",
    ],
    workflows: [
      "Job order runs → BEEDIGIT batches labels for the full run → the printer produces the configured label batch",
    ],
    industries: ["Cable & wire", "Plastics extrusion", "Metal processing", "Automotive components", "Packaging"],
    compatibleModules: [modules.mes, modules.quality],
    deploymentNotes: "Configured for the customer's label templates, media and network policy.",
    relatedProductSlugs: ["beeprint-desktop", "beeprint-scan-apply", "beeprint-rfid"],
    sectionHeadings: {
      purpose: "Industrial label production",
      workflows: "Typical printing workflows",
      deployment: "Recommended deployment environments",
    },
  },
  {
    slug: "beeprint-mobile",
    name: "BEEPRINT Mobile",
    family: "Mobile label printer",
    category: "Printing & Labeling",
    eyebrow: "Battery-powered mobile printer",
    shortDescription: "Mobile label printer for printing at the point of work, warehouse and yard.",
    fullDescription:
      "BEEPRINT Mobile is a battery-powered mobile label printer used where operators cannot come to a fixed printer — warehouse aisles, maintenance rounds, outdoor operations and inventory work. It pairs with the operator's mobile terminal and prints on demand.",
    heroImage: printMobile.url,
    keyBenefits: [
      "Print exactly where the label is needed",
      "Fewer trips to the label printer",
      "Immediate correction of mislabeled items",
      "Rugged for warehouse and yard use",
    ],
    capabilities: [
      { title: "Mobile printing", desc: "Battery-powered for a full shift, depending on configuration." },
      { title: "Wireless pairing", desc: "Pairs with BEEDIGIT mobile terminals." },
      { title: "Rugged design", desc: "Built for warehouse, maintenance and outdoor use." },
    ],
    specifications: [
      { label: "Type", value: "Mobile battery-powered label printer" },
      { label: "Connectivity", value: "Bluetooth and Wi-Fi options, depending on configuration" },
      { label: "Battery", value: "Shift-long use, depending on configuration" },
      { label: "Durability", value: "Industrial rated, depending on configuration" },
    ],
    connectivity: ["Bluetooth", "Wi-Fi (optional)"],
    useCases: [
      "Warehouse relabeling in the aisle",
      "Inventory and cycle-count labels",
      "Maintenance tags on assets",
      "Material movement labels",
      "Outdoor and yard operations",
    ],
    workflows: [
      "Operator finds mislabeled item → prints a corrected label at the shelf and applies it immediately",
    ],
    industries: ["Logistics", "Manufacturing groups", "Food & beverage", "Metal processing"],
    compatibleModules: [modules.mes, modules.cmms],
    deploymentNotes: "Enrolled and preconfigured for the customer's roles, label templates and workflows.",
    relatedProductSlugs: ["beeprint-desktop", "beeprint-industrial", "beepad-mobile"],
  },
  {
    slug: "beeprint-scan-apply",
    name: "BEEPRINT Scan & Apply",
    family: "Scan-print-apply device",
    category: "Printing & Labeling",
    eyebrow: "All-in-one scan / print / apply",
    shortDescription: "Handheld device that combines scanning, printing and label application in one continuous workflow.",
    fullDescription:
      "BEEPRINT Scan & Apply is a handheld all-in-one device that combines scanning, printing and label application in one continuous workflow. It reduces walking and repeated label handling on high-volume, variable-SKU operations. RFID encoding is available on selected configurations.",
    heroImage: printScanApply.url,
    keyBenefits: [
      "One handheld workflow without changing tools",
      "Reduces walking and repeated label handling",
      "Designed for small-lot and multi-batch operations",
      "Optional RFID encoding for smart labels",
    ],
    capabilities: [
      { title: "Scan-print-apply", desc: "Complete the labeling workflow without putting the tool down." },
      { title: "Configurable label format", desc: "Supports label formats defined by the selected device configuration." },
      { title: "Optional RFID encoding", desc: "RFID encoding is available on selected configurations." },
    ],
    specifications: [
      { label: "Type", value: "Handheld scan / print / apply device" },
      { label: "Label format", value: "Supports label formats defined by the selected device configuration" },
      { label: "RFID encoding", value: "Available on selected configurations" },
      { label: "Connectivity", value: "Wi-Fi connectivity; Bluetooth available on selected configurations" },
    ],
    connectivity: ["Wi-Fi", "Bluetooth (optional)"],
    useCases: [
      "Rapid relabeling of large batches",
      "Pallet and large-item labeling",
      "Multi-batch, small-lot production",
      "Warehouse exception handling",
      "Scan-print-apply workflows on packaging lines",
    ],
    workflows: [
      "Scan item → device prints the correct label → operator prints and applies the correct label without changing devices",
    ],
    industries: ["Logistics", "Food & beverage", "Packaging", "Manufacturing groups"],
    compatibleModules: [modules.mes, modules.quality],
    deploymentNotes: "Preconfigured for the customer's label templates and workflow.",
    relatedProductSlugs: ["beeprint-mobile", "beeprint-industrial", "beeprint-rfid"],
    sectionHeadings: {
      purpose: "How scan–print–apply works",
      workflows: "Labeling workflows",
      deployment: "Recommended operating environments",
    },
  },
  {
    slug: "beeprint-rfid",
    name: "BEEPRINT RFID",
    family: "RFID label printer",
    category: "Printing & Labeling",
    eyebrow: "RFID label printer",
    shortDescription: "Prints human-readable and barcode information while encoding UHF RFID tags.",
    fullDescription:
      "BEEPRINT RFID is a label printer that prints human-readable text and barcodes while encoding UHF RFID tags on the same label. It is the practical entry point into RFID projects — one device produces labels that work for scanning, human reading and automated RFID detection.",
    heroImage: printRfid.url,
    keyBenefits: [
      "One label, three ways to read it",
      "Simplifies RFID rollouts",
      "Consistent encoding tied to BEEDIGIT data",
      "Foundation for smart WIP and asset tracking",
    ],
    capabilities: [
      { title: "Print and encode", desc: "Prints and writes RFID tags in one pass." },
      { title: "Human + machine readable", desc: "Text, barcode and RFID on a single label." },
      { title: "UHF RFID printing and encoding", desc: "Encodes UHF RFID tags with IDs that BEEDIGIT already manages." },
    ],
    specifications: [
      { label: "Type", value: "UHF RFID label printer" },
      { label: "Print width", value: "Up to 4-inch, depending on configuration" },
      { label: "Connectivity", value: "USB, Ethernet; Wi-Fi optional, depending on configuration" },
    ],
    connectivity: ["USB", "Ethernet", "Wi-Fi (optional)"],
    useCases: [
      "RFID asset labels",
      "Smart WIP tags",
      "Returnable packaging identification",
      "Pallet and container identification",
      "Automated traceability projects",
    ],
    workflows: [
      "BEEDIGIT generates ID → printer writes tag and prints label → item joins the RFID-tracked flow",
    ],
    industries: ["Automotive components", "Logistics", "Manufacturing groups", "Metal processing"],
    compatibleModules: [modules.mes, modules.quality],
    deploymentNotes: "Configured for the customer's tag type, label templates and BEEDIGIT ID strategy.",
    relatedProductSlugs: ["beeprint-industrial", "beerfid-station", "beerfid-gate"],
  },
  {
    slug: "beescreen-wide",
    name: "BEESCREEN Wide",
    family: "Ultra-wide industrial display",
    category: "Industrial Displays",
    eyebrow: "Industrial display",
    shortDescription:
      "Ultra-wide industrial display for live line status, OEE, output and bottleneck visualization.",
    positioning: "See the entire production line in one continuous live view.",
    fullDescription:
      "BEESCREEN Wide is an ultra-wide industrial display built for BEEDIGIT Line View. It shows the entire production line in one continuous live view, so operators and supervisors see connected work centers, current states, OEE, shift output against target, material waits and bottlenecks without switching screens. The catalog configuration is the 48-inch wide-bar model with approximate overall dimensions of 1218×248 mm, mounted on a wall, overhead or line-side depending on the installation.",
    heroImage: screenWide.url,
    imageAlt:
      "BEESCREEN Wide industrial display showing the BEEDIGIT cable production line view",
    gallery: [
      { src: screenWide.url, caption: "Live BEEDIGIT view", alt: "BEESCREEN Wide industrial display showing the BEEDIGIT cable production line view" },
      { src: screenWideAngle.url, caption: "Three-quarter view", alt: "BEESCREEN Wide industrial display seen from a three-quarter angle" },
      { src: screenWideRear.url, caption: "Industrial enclosure", alt: "Rear of the BEESCREEN Wide industrial display enclosure" },
      { src: screenWideContext.url, caption: "48-inch factory office installation", alt: "48-inch BEESCREEN Wide installed in a factory office" },
    ],
    keyBenefits: [
      "One continuous live view of the whole line",
      "Shift output against target at a glance",
      "Bottlenecks and material waits made visible",
      "Full-width Andon alerts for faster response",
    ],
    capabilities: [
      { title: "Panoramic Line View", desc: "Panoramic BEEDIGIT production-line visualization across connected work centers." },
      { title: "Live OEE", desc: "Live work-center state, OEE and shift-output display." },
      { title: "Andon Visibility", desc: "Full-width Andon alerts and bottleneck visibility." },
      { title: "Flexible mounting", desc: "Wall, overhead or line-side mounting depending on configuration." },
      { title: "Configured per site", desc: "Screen size, brightness and connectivity selected for each installation." },
    ],
    specifications: [
      { label: "Configuration shown", value: "48-inch wide-bar model" },
      { label: "Overall dimensions", value: "Approximately 1218×248 mm" },
      { label: "Brightness", value: "Selected during project design" },
      { label: "Connectivity", value: "Depending on configuration" },
      { label: "Mounting", value: "Wall, overhead or line-side, depending on configuration" },
      { label: "Content source", value: "BEEDIGIT Line View" },
    ],
    connectivity: ["Ethernet", "Wi-Fi (depending on configuration)", "BEEDIGIT Line View streaming"],
    useCases: [
      "Cable drawing, stranding, extrusion, screening and sheathing line overview",
      "End-to-end status visualization for long production lines",
      "Shift output versus target boards",
      "Bottleneck and material-wait visibility",
      "Line-side Andon and escalation display",
      "Supervisor overview in production control areas",
    ],
    workflows: [
      "Machine signals → BEEEDGE / BEEDIGIT HUB → BEEDIGIT Line View → live BEESCREEN Wide visualization → supervisor response",
    ],
    industries: ["Cable & wire", "Extrusion", "Metal processing", "Manufacturing groups"],
    compatibleModules: [modules.oee, modules.mes, modules.quality, modules.cmms, modules.energy, modules.dashboards],
    deploymentNotes:
      "Installed where a full line must be understood at a glance — above the line, at the line side or in production control areas. Mounting, screen size and connectivity are selected during project design.",
    relatedProductSlugs: ["beeedge", "beedigit-hub", "beepad-industrial"],
    sectionHeadings: {
      purpose: "About BEESCREEN Wide",
      workflows: "BEESCREEN Wide in the workflow",
      deployment: "Where BEESCREEN Wide is deployed",
    },
  },
  {
    slug: "beescreen-ip66",
    name: "BEESCREEN IP66",
    family: "Sealed industrial display",
    category: "Industrial Displays",
    eyebrow: "Industrial display",
    shortDescription:
      "Sealed IP66 display for BEEDIGIT dashboards in dusty, wet and demanding production areas.",
    positioning: "Bring live BEEDIGIT visibility into demanding production environments.",
    fullDescription:
      "BEESCREEN IP66 is a sealed industrial display that brings live BEEDIGIT visibility into demanding production environments. Its IP66 enclosure protects against dust ingress and water jets, which makes it suitable for dusty cable and wire areas and wet or washdown-adjacent zones. IP66 does not imply suitability for submersion or chemical washdown unless separately confirmed. The catalog configuration is the 55-inch model, showing BEEDIGIT work-center cards, OEE and production dashboards.",
    heroImage: screenIp66.url,
    imageAlt:
      "BEESCREEN IP66 sealed industrial display showing the BEEDIGIT work-center dashboard",
    gallery: [
      { src: screenIp66.url, caption: "Live BEEDIGIT view", alt: "BEESCREEN IP66 sealed industrial display showing the BEEDIGIT work-center dashboard" },
      { src: screenIp66Angle.url, caption: "Three-quarter view", alt: "BEESCREEN IP66 sealed industrial display seen from a three-quarter angle" },
      { src: screenIp66Rear.url, caption: "Industrial enclosure", alt: "Rear of the BEESCREEN IP66 sealed industrial enclosure" },
      { src: screenIp66Context.url, caption: "55-inch shop-floor installation", alt: "55-inch BEESCREEN IP66 installed on the shop floor" },
    ],
    keyBenefits: [
      "Sealed against dust ingress and water jets",
      "Large-format BEEDIGIT dashboards on the shop floor",
      "Shared shift visibility for whole teams",
      "Faster escalation on quality, maintenance and material waits",
    ],
    capabilities: [
      { title: "IP66 Enclosure", desc: "Sealed enclosure protecting against dust ingress and water jets." },
      { title: "Live Work Centers", desc: "BEEDIGIT work-center cards, OEE and production dashboards." },
      { title: "Industrial Visibility", desc: "Large-format shift status and output visualization." },
      { title: "Display options", desc: "High-brightness and UHD options depending on configuration." },
      { title: "Installation options", desc: "Wall, line-side or outdoor-facing installation depending on project validation." },
    ],
    specifications: [
      { label: "Configuration shown", value: "55-inch display" },
      { label: "Protection rating", value: "IP66 — dust ingress and water jets" },
      { label: "Enclosure dimensions", value: "Depending on configuration" },
      { label: "Brightness / resolution", value: "High-brightness and UHD options depending on configuration" },
      { label: "Connectivity", value: "Selected during project design" },
      { label: "Mounting", value: "Wall, line-side or outdoor-facing, subject to project validation" },
    ],
    connectivity: ["Ethernet", "Wi-Fi (depending on configuration)", "BEEDIGIT dashboards"],
    useCases: [
      "Dusty cable and wire production areas",
      "Wet or washdown-adjacent production zones",
      "Large work-center status boards",
      "Shift OEE and output visualization",
      "Quality, maintenance and material-wait alerts",
      "Covered outdoor or exposed logistics areas after project validation",
    ],
    workflows: [
      "Work-center data → BEEDIGIT dashboard → BEESCREEN IP66 → shared shift visibility → faster escalation",
    ],
    industries: ["Cable & wire", "Food & beverage", "Chemicals", "Logistics"],
    compatibleModules: [modules.oee, modules.mes, modules.quality, modules.cmms, modules.energy, modules.dashboards],
    deploymentNotes:
      "Deployed where standard displays cannot survive: dusty production halls, wet or washdown-adjacent zones and covered outdoor areas after project validation.",
    relatedProductSlugs: ["beeedge", "beedigit-hub", "beepad-industrial"],
    sectionHeadings: {
      purpose: "About BEESCREEN IP66",
      workflows: "BEESCREEN IP66 in the workflow",
      deployment: "Where BEESCREEN IP66 is deployed",
    },
  },
];

export const getHardwareProduct = (slug: string) =>
  HARDWARE_PRODUCTS.find((p) => p.slug === slug);

export const getRelatedProducts = (slugs: string[]) =>
  slugs
    .map((s) => HARDWARE_PRODUCTS.find((p) => p.slug === s))
    .filter((p): p is HardwareProduct => Boolean(p));